import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import DarkLogo from '../../static/home/DarkLogo2.png';
import LightLogo from '../../static/home/LightLogo.png';
import { FaMoon } from 'react-icons/fa';
import { IoSunnyOutline } from 'react-icons/io5';
import Career from './Career';
import {motion} from 'framer-motion'

const Header = ({ darkMode, toggleTheme }) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const location = useLocation();
	const history = useHistory();

	useEffect(() => {
		const handleScroll = () => {
			setScrolled(window.scrollY > 20);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const style2 = `
		@keyframes gradientAnimation {
		  0% {
			background-position: 0% 50%;
		  }
		  50% {
			background-position: 100% 50%;
		  }
		  100% {
			background-position: 0% 50%;
		  }
		}

		.animated-gradient-text {
		  background: linear-gradient(270deg, #2161ed, #7199f1, #2161ed);
		  background-size: 200% 200%;
		  animation: gradientAnimation 5s ease infinite;
		  -webkit-background-clip: text;
		  -webkit-text-fill-color: transparent;
		  background-clip: text;
		  text-fill-color: transparent;
		}

		.half-gradient-text {
		  background: linear-gradient(90deg, #2161ed, #7199f1, #2161ed);
		  background-size: 200% 200%;
		  animation: gradientAnimation 5s ease infinite;
		  -webkit-background-clip: text;
		  -webkit-text-fill-color: transparent;
		  background-clip: text;
		  text-fill-color: transparent;
		}
	  `;

	const handleNavigation = (path) => {
		// Check if we're on register page
		if (location.pathname === '/register') {
			history.push('/');
			// Wait for navigation to complete before scrolling
			setTimeout(() => {
				const element = document.getElementById(path);
				if (element) {
					element.scrollIntoView({ behavior: 'smooth' });
				}
			}, 100);
			return;
		}

		// Existing navigation logic
		if (location.pathname === '/know-more') {
			history.push('/');
			setTimeout(() => {
				const element = document.getElementById(path);
				if (element) {
					element.scrollIntoView({ behavior: 'smooth' });
				}
			}, 100);
		} else {
			const element = document.getElementById(path);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		}
	};

	const handleLogoClick = () => {
		if (location.pathname !== '/') {
			history.push('/');
		} else {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	// Add register button to navigation
	const navigationItems = [
		{ name: 'Home', action: handleLogoClick },
		{ name: 'About Us', action: () => handleNavigation('about-us') },
		{ name: 'Highlights', action: () => handleNavigation('highlights') },
		{ name: 'Team', action: () => handleNavigation('team') },
		{ name: 'Clients', action: () => handleNavigation('clients') },
		{ name: 'Career', action: () => window.location.href='/career' },
	];

	const registerButton = (
		<button
			onClick={() => {
				location.pathname === '/register' ? history.push('/') : history.push('/register');
			}}
			className={`px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 
        text-white hover:opacity-90 transition-all duration-300`}>
			{location.pathname === '/register' ? 'Back to Home' : 'Register Now'}
		</button>
	);

	return (
		<header
			className={`fixed top-0 left-0 w-full z-50 py-3 transition-all duration-300 
			${scrolled ? 'backdrop-blur-lg bg-white/90 dark:bg-black/90 shadow-lg' : 'bg-transparent'}
			${darkMode ? 'dark' : ''}`}>
			<div className='container mx-auto px-6 flex justify-between items-center'>
				<h1 onClick={handleLogoClick} className='text-2xl sm:text-4xl font-extrabold flex items-center gap-2 font-poppins cursor-pointer'>
					<style>{style2}</style>
					<img src={darkMode ? DarkLogo : LightLogo} alt='Necttos Logo' className='w-12 h-12 transform transition-transform duration-300 hover:scale-110 rounded-xl' />
					<span className='half-gradient-text font-poppins'>Necttos</span>
				</h1>

				<motion.nav
				initial={{ opacity: 0, y: -250 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 1, type:'spring', stiffness:100 }}				 
				className='hidden md:flex items-center space-x-6'>
					{navigationItems.map((item) => (
						<button
							key={item.name}
							onClick={item.action}
							className={`text-md font-base transition-colors duration-200 hover-underline-animation
                ${darkMode ? 'text-gray-300 hover:text-blue-400' : 'text-gray-700 hover:text-blue-500'}`}>
							{item.name}
						</button>
					))}

					{/* Register Button */}
					{location.pathname !== '/register' && registerButton}

					{/* Theme Toggle */}
					<button onClick={toggleTheme} className='text-lg'>
						{darkMode ? <IoSunnyOutline /> : <FaMoon />}
					</button>
				</motion.nav>

				<div className='md:hidden flex gap-4 items-center'>
					{/* {registerButton} */}
					<button onClick={toggleTheme} className='text-lg'>
						{darkMode ? <IoSunnyOutline /> : <FaMoon />}
					</button>
					<button className=' flex items-center' onClick={() => setMenuOpen(!menuOpen)} aria-label='Toggle menu'>
						<svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
							<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d={menuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16m-7 6h7'} />
						</svg>
					</button>
				</div>
			</div>

			<div
				className={`md:hidden transition-all duration-500 ease-in-out overflow-hidden 
					${menuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0 pointer-events-none'} 
					${darkMode ? 'bg-black border-t border-gray-800' : 'bg-white border-t border-gray-200'}`}>
				<nav className='flex flex-col items-start space-y-4 px-6 py-4'>
					{navigationItems.map((item) => (
						<button
							key={item.name}
							onClick={() => {
								item.action();
								setMenuOpen(false);
							}}
							className={`text-lg font-medium transition-colors duration-200 hover-underline-animation
                ${darkMode ? 'text-gray-300 hover:text-blue-400' : 'text-gray-700 hover:text-blue-500'}`}>
							{item.name}
						</button>
					))}
				</nav>
			</div>
		</header>
	);
};

export default Header;
